// Paint plan: the single wall-color engine, used by BOTH the browser
// preview and the Node demo (engines/paint.ts is now a thin wrapper over
// this). Coordinated and explainable:
//
//  - COORDINATION: it takes the room's floor temperature and evaluates
//    W-01 (warm/cool harmony) against it, graded and continuous. The wall
//    is genuinely chosen to bridge the floor, not picked in isolation and
//    described as if it were. This is the fix for the "isolated picks" and
//    "false reasoning trace" findings.
//  - RULES AS DATA: rule weights and thresholds come from designRules.ts
//    (the shared source, mirrored by design-laws.yaml), so tuning a weight
//    changes the pick. W-01, L-01, L-02 all score from data.
//  - HONEST BLEND: the design-principle score is an ABSOLUTE 0..1 (a
//    weighted average of graded rule terms + the room-usage preference),
//    so `score = principle*(1-pull) + styleFit*pull` makes stylePull a
//    real share. No min-max normalization distorting the 75/25.
//  - EXPLAINED: every plan ships a computed confidence, an impact badge,
//    and a reasoning trace generated from the scoring itself.
//  - SELECTABLE: targetLrv steers the pick toward a chosen lightness (it
//    outweighs the soft L-01/L-02 floors, which then only warn, and it
//    tells the vibe to stop steering lightness); sheenOverride sets the
//    sheen and flags an S-02 wet-room violation instead of hiding it.
//
// Reuses color.ts for effective temperature, Delta E, and the sheen ladder.

import paintData from "../../data/seed/paints.json" with { type: "json" };
import { DEFAULT_STYLE_PULL } from "./types.js";
import type { RoomType, Orientation, DaylightLevel, Objective, StyleKey, ImpactBadge } from "./types.js";
import { styleFit } from "./style.js";
import { ruleWeight, ruleParam } from "./designRules.js";
import {
  effectiveTemperature,
  deltaE,
  hexToLab,
  SHEEN_LADDER,
  sheenStep,
  type Sheen,
} from "./color.js";

interface SeedColor {
  brand: string; code: string; name: string; hex: string; lrv: number;
  temperature: number; undertone_family: string; chroma_opinion: number;
}
interface SeedLine {
  line_id: string; brand: string; name: string;
  durability_class: string; rental_suitability: number; price_per_gallon: number;
}

const COLORS = (paintData as { colors: SeedColor[] }).colors;
const LINES = (paintData as { product_lines: SeedLine[] }).product_lines;

// Room usage -> mood. Makes a bedroom calm and a dining room lively without
// the user spelling it out. Mood is a PREFERENCE, not a design law, so it
// carries a moderate weight below the heavy rules (W-01 must outrank it).
export type Mood = "bright" | "lively" | "inviting" | "calming" | "spa";
const MOOD_WEIGHT = 6.0;
// An explicit user LRV target is a deliberate choice, so it outweighs the
// soft L-01/L-02 floors (which then only warn, not override). It stays below
// W-01 harmony, so a target can't force a floor-clashing color.
const TARGET_LRV_WEIGHT = 10.0;

export const ROOM_MOOD: Record<RoomType, Mood> = {
  kitchen: "bright", bath: "spa", bedroom: "calming", living: "inviting",
  dining: "lively", laundry: "bright", office: "inviting", other: "inviting",
};

export const MOOD_COPY: Record<Mood, string> = {
  bright: "Bright and clean. High-LRV whites bounce task light and read fresh.",
  lively: "Lively and intimate. Warm mid tones or a deeper feature wall carry evening gatherings.",
  inviting: "Warm and inviting. Greige and warm neutrals wrap the room without going dark.",
  calming: "Calming and restful. Soft, low-chroma tones lower visual noise for sleep.",
  spa: "Crisp and spa-like. Cool-leaning neutrals stay clean under vanity light.",
};

const MOOD_TARGET: Record<Mood, { lrv: [number, number]; temp: number; chroma: number }> = {
  bright: { lrv: [78, 92], temp: 0.15, chroma: 0.2 },
  lively: { lrv: [55, 82], temp: 0.28, chroma: 0.3 },
  inviting: { lrv: [58, 80], temp: 0.32, chroma: 0.28 },
  calming: { lrv: [55, 76], temp: -0.02, chroma: 0.24 },
  spa: { lrv: [60, 86], temp: -0.1, chroma: 0.34 },
};

export interface PaintPlanInput {
  roomType: RoomType;
  orientation: Orientation;
  bulbCCT: number; // from the lighting plan, so color is judged under the bulb
  daylight: DaylightLevel;
  objective: Objective;
  sqft: number;
  floorTemperature?: number; // the resolved floor's temperature, for W-01
  styleVibe?: StyleKey;
  stylePull?: number; // 0..1; how hard the vibe steers (default 0.25)
  // User-selectable factors. targetLrv steers the pick toward a lightness
  // instead of overriding the exact color; sheenOverride sets the wall sheen.
  targetLrv?: number;
  sheenOverride?: Sheen;
}

export interface ColorPick {
  brand: string; code: string; name: string; hex: string; lrv: number;
  undertone: string; chroma: number; effTemp: number;
  tempLabel: "warm" | "cool" | "neutral";
}

export interface RankedColor { name: string; code: string; score: number; hardFiltered: boolean; }

export interface PaintPlan {
  mood: Mood;
  moodCopy: string;
  wall: ColorPick;
  line: SeedLine;
  runnerUp: ColorPick;
  baseSheen: Sheen;
  wallSheen: Sheen;
  recommendedSheen: Sheen; // the engine's sheen, pre-override
  sheenBelowWetFloor: boolean; // true if an override sits under the S-02 floor
  trimSheen: Sheen;
  steppedDown: boolean;
  lrvFloor: number;
  targetLrv: number | null; // the active LRV target, if the user set one
  harmony: { gap: number; score: number } | null; // W-01 vs the floor, null if no floor given
  confidence: number; // 0-100, computed from the scoring trace
  badge: ImpactBadge; // computed stakes ("does this decision matter")
  ranked: RankedColor[]; // top field, for a tradeoff view
  hardFilteredCount: number; // removed by W-01 hard filter
  reasoning: string[];
}

function tempLabel(t: number): ColorPick["tempLabel"] {
  return t > 0.12 ? "warm" : t < -0.12 ? "cool" : "neutral";
}

function durableLineFor(brand: string): SeedLine {
  const byBrand = LINES.filter((l) => l.brand === brand);
  const pool = byBrand.length ? byBrand : LINES;
  const classA = pool.filter((l) => l.durability_class === "A");
  const ranked = (classA.length ? classA : pool).sort((a, b) => b.rental_suitability - a.rental_suitability);
  return ranked[0];
}

interface Scored {
  c: SeedColor; effT: number;
  moodFit: number; harmony: number | null; lrvSat: number | null;
  principle: number; score: number; hardFiltered: boolean;
}

// Absolute 0..1 sub-scores, combined by rule weight from designRules.
function scoreField(input: PaintPlanInput, mood: Mood, lrvFloor: number): Scored[] {
  const t = MOOD_TARGET[mood];
  const [lo, hi] = t.lrv;
  const mid = (lo + hi) / 2;

  const hasFloor = input.floorTemperature !== undefined;
  const w01Gap = ruleParam("W-01", "max_temperature_gap", 0.9);
  const w01w = ruleWeight("W-01");
  // The active LRV floor comes from whichever of L-01/L-02 applies; weight it
  // by that rule so a small-room floor (L-02) and a low-light floor (L-01)
  // carry their own data-driven importance.
  const lrvw = Math.max(ruleWeight("L-01"), ruleWeight("L-02"));

  const pull = input.styleVibe ? Math.max(0, Math.min(1, input.stylePull ?? DEFAULT_STYLE_PULL)) : 0;

  return COLORS.map((c) => {
    const effT = effectiveTemperature(c.temperature, input.orientation, input.bulbCCT);

    // room-usage preference (0..1). When the user sets an explicit LRV
    // target, lightness leaves the mood blend and becomes its own weighted
    // term (below); the mood then only speaks to temperature and chroma.
    const hasTarget = input.targetLrv !== undefined;
    const lrvProximity = hasTarget ? Math.max(0, 1 - Math.abs(c.lrv - (input.targetLrv as number)) / 25) : 0;
    const lrvBand = c.lrv >= lo && c.lrv <= hi ? 1 : Math.max(0, 1 - Math.abs(c.lrv - mid) / 40);
    const tempFit = 1 - Math.min(1, Math.abs(effT - t.temp));
    const chromaFit = c.chroma_opinion <= t.chroma ? 1 : Math.max(0, 1 - (c.chroma_opinion - t.chroma) / 0.3);
    const soothing = (mood === "spa" || mood === "calming") &&
      (c.undertone_family === "green" || c.undertone_family === "blue") ? 0.1 : 0;
    const moodFit = hasTarget
      ? Math.min(1, (tempFit + chromaFit) / 2 + soothing)
      : Math.min(1, (lrvBand + tempFit + chromaFit) / 3 + soothing);

    // W-01 harmony vs the floor (0..1), graded and continuous
    let harmony: number | null = null;
    let hardFiltered = false;
    if (hasFloor) {
      const gap = Math.abs(effT - (input.floorTemperature as number));
      harmony = Math.max(0, 1 - gap / w01Gap);
      if (gap > w01Gap) hardFiltered = true; // W-01 weight 9.8 -> hard filter
    }

    // L-01 / L-02 LRV floor satisfaction (0..1), graded
    let lrvSat: number | null = null;
    if (lrvFloor > 0) {
      lrvSat = c.lrv >= lrvFloor ? 1 : Math.max(0, 1 - (lrvFloor - c.lrv) / 25);
    }

    // absolute principle score: weighted average of the active terms. When
    // the user set an explicit LRV target, lightness is governed by that
    // target alone; L-01/L-02 stop competing (they become a heads-up in the
    // trace) so the target is not fighting the very floor it means to override.
    let wsum = MOOD_WEIGHT, vsum = moodFit * MOOD_WEIGHT;
    if (hasTarget) { wsum += TARGET_LRV_WEIGHT; vsum += lrvProximity * TARGET_LRV_WEIGHT; }
    if (harmony !== null) { wsum += w01w; vsum += harmony * w01w; }
    if (lrvSat !== null && !hasTarget) { wsum += lrvw; vsum += lrvSat * lrvw; }
    const principle = vsum / wsum;

    const fit = input.styleVibe
      ? styleFit({ name: c.name, lrv: c.lrv, temperature: effT, chroma: c.chroma_opinion }, input.styleVibe, "wall", hasTarget)
      : 0;
    const score = principle * (1 - pull) + fit * pull;

    return { c, effT, moodFit, harmony, lrvSat, principle, score, hardFiltered };
  });
}

function toPick(x: { c: SeedColor; effT: number }): ColorPick {
  return {
    brand: x.c.brand, code: x.c.code, name: x.c.name, hex: x.c.hex,
    lrv: x.c.lrv, undertone: x.c.undertone_family, chroma: x.c.chroma_opinion,
    effTemp: x.effT, tempLabel: tempLabel(x.effT),
  };
}

function computeConfidence(winner: Scored, margin: number): number {
  // rule agreement: the winner's own weighted rule-term satisfaction
  let wsum = 0, vsum = 0;
  if (winner.harmony !== null) { const w = ruleWeight("W-01"); wsum += w; vsum += winner.harmony * w; }
  if (winner.lrvSat !== null) { const w = Math.max(ruleWeight("L-01"), ruleWeight("L-02")); wsum += w; vsum += winner.lrvSat * w; }
  const ruleAgreement = wsum > 0 ? vsum / wsum : 1;
  const dataCoverage = 1; // full attributes on every seed color
  return Math.round(100 * (0.5 * ruleAgreement + 0.3 * margin + 0.2 * dataCoverage));
}

function computeBadge(top: Scored, alt: Scored, hardFilteredCount: number, harmonySpread: number): ImpactBadge {
  const dE = deltaE(hexToLab(top.c.hex), hexToLab(alt.c.hex));
  const marginGap = top.score - alt.score;
  if (marginGap < 0.04 && dE < 3) return "minor"; // two near-identical whites: say it doesn't matter
  if (hardFilteredCount > 0 || harmonySpread > 0.5) return "foundational"; // temperature family did real work
  return "important";
}

export function planWallColor(input: PaintPlanInput): PaintPlan {
  const mood = ROOM_MOOD[input.roomType] ?? "inviting";
  const wet = input.roomType === "kitchen" || input.roomType === "bath" || input.roomType === "laundry";

  // L-01 low light -> LRV >= min_lrv; L-02 small room -> LRV >= min_lrv.
  const lowLight = input.orientation === "north" || input.daylight === "low";
  const small = input.sqft < ruleParam("L-02", "max_sqft", 100);
  const lrvFloor = Math.max(
    lowLight ? ruleParam("L-01", "min_lrv", 60) : 0,
    small ? ruleParam("L-02", "min_lrv", 65) : 0
  );

  const field = scoreField(input, mood, lrvFloor);
  const survivors = field.filter((x) => !x.hardFiltered).sort((a, b) => b.score - a.score);
  const hardFilteredCount = field.length - survivors.length;
  // If W-01 somehow filtered everything (pathological floor), fall back to
  // the full field so the engine still answers rather than throwing.
  const ranked = survivors.length ? survivors : [...field].sort((a, b) => b.score - a.score);

  const winner = ranked[0];
  const runnerUp = ranked[1] ?? ranked[0];
  const top = toPick(winner);
  const alt = toPick(runnerUp);

  const line = durableLineFor(top.brand);
  const durable = line.durability_class === "A";

  const baseSheen: Sheen = input.roomType === "bath" ? "semi-gloss" : wet ? "satin" : "eggshell";
  const wetFloorSheen: Sheen = wet ? (input.objective === "rental" ? "satin" : "eggshell") : "flat";
  let recommendedSheen: Sheen = baseSheen;
  if (durable) {
    const dropped = SHEEN_LADDER[Math.max(0, sheenStep(baseSheen) - 1)];
    recommendedSheen = sheenStep(dropped) >= sheenStep(wetFloorSheen) ? dropped : wetFloorSheen;
  }
  // The user's sheen choice wins if set; we still flag it when it drops below
  // the S-02 wet-room floor rather than silently overriding the rule.
  const wallSheen: Sheen = input.sheenOverride ?? recommendedSheen;
  const sheenBelowWetFloor = wet && sheenStep(wallSheen) < sheenStep(wetFloorSheen);
  const steppedDown = sheenStep(wallSheen) < sheenStep(baseSheen);
  const trimSheen = SHEEN_LADDER[Math.min(SHEEN_LADDER.length - 1,
    Math.max(sheenStep(wallSheen) + 1, sheenStep("satin")))];

  const margin = Math.min(1, (winner.score - runnerUp.score) / 0.15);
  const confidence = computeConfidence(winner, margin);
  const harmonyVals = field.map((x) => x.harmony).filter((h): h is number => h !== null);
  const harmonySpread = harmonyVals.length ? Math.max(...harmonyVals) - Math.min(...harmonyVals) : 0;
  const badge = computeBadge(winner, runnerUp, hardFilteredCount, harmonySpread);

  // ---- Reasoning, generated from the trace (no claim without a computation) ----
  const reasoning: string[] = [];
  reasoning.push(`Room usage sets the mood. ${MOOD_COPY[mood]}`);
  if (input.targetLrv !== undefined) {
    reasoning.push(
      `You asked for LRV near ${input.targetLrv}; ${top.name} at LRV ${top.lrv} is the closest color that still holds harmony and the rule floors, reading ${top.tempLabel} under ${input.bulbCCT}K ${input.orientation} light.`
    );
  } else {
    reasoning.push(
      `${top.name} at LRV ${top.lrv} sits in the ${MOOD_TARGET[mood].lrv[0]}-${MOOD_TARGET[mood].lrv[1]} band this room wants, reading ${top.tempLabel} under ${input.bulbCCT}K ${input.orientation} light.`
    );
  }

  let harmony: PaintPlan["harmony"] = null;
  if (winner.harmony !== null && input.floorTemperature !== undefined) {
    const gap = Math.abs(winner.effT - input.floorTemperature);
    harmony = { gap: +gap.toFixed(2), score: +winner.harmony.toFixed(2) };
    const floorWord = input.floorTemperature > 0.12 ? "warm" : input.floorTemperature < -0.12 ? "cool" : "neutral";
    if (winner.harmony >= 0.75) {
      reasoning.push(`Harmonizes with the ${floorWord} floor (W-01, weight ${ruleWeight("W-01")}): temperature gap ${harmony.gap}, harmony ${harmony.score}. This is a real check against the resolved floor, not an assumption.`);
    } else {
      reasoning.push(`Runs ${tempLabel(winner.effT)} against a ${floorWord} floor (W-01): gap ${harmony.gap}, harmony ${harmony.score}. It bridges rather than fights, but a warmer greige would sit easier if you want them closer.`);
    }
    if (hardFilteredCount > 0) {
      reasoning.push(`${hardFilteredCount} color${hardFilteredCount === 1 ? "" : "s"} were hard-filtered for clashing with the floor beyond W-01's ${ruleParam("W-01", "max_temperature_gap", 0.9)} gap.`);
    }
  }

  if (lrvFloor > 0) {
    const parts: string[] = [];
    if (lowLight) parts.push("low natural light (L-01)");
    if (small) parts.push("a sub-100 sqft room (L-02)");
    if (top.lrv >= lrvFloor) {
      reasoning.push(`${parts.join(" and ")} set an LRV floor of ${lrvFloor}; at LRV ${top.lrv} this clears it.`);
    } else {
      const driver = input.targetLrv !== undefined ? `Your LRV target of ${input.targetLrv}` : "Your vibe";
      reasoning.push(`Heads up: ${parts.join(" and ")} set an LRV floor of ${lrvFloor}, and at LRV ${top.lrv} this sits below it. ${driver} pulled it darker than the livability guideline, so expect a moodier, dimmer room than the rule would pick.`);
    }
  }

  if (input.sheenOverride) {
    reasoning.push(
      sheenBelowWetFloor
        ? `You set ${wallSheen} sheen (recommended ${recommendedSheen}). Heads up: in a ${input.roomType} that sits below the S-02 wet-room floor of ${wetFloorSheen}, so it will not scrub as well near splashes.`
        : `You set ${wallSheen} sheen (recommended ${recommendedSheen}); trim recomputes above it.`
    );
  } else if (wet) {
    reasoning.push(
      `Base sheen for a ${input.roomType} is ${baseSheen} for moisture and scrubbing. ${
        durable
          ? `Because ${line.name} is a class-A scuff and scrub product, you can step down to ${wallSheen} and keep the washability, while hiding wall flaws and cutting glare (satisfies R-01 by the line, S-02 by the sheen).`
          : `Hold at ${baseSheen} for washability.`
      }`
    );
  } else {
    reasoning.push(
      `Walls run ${wallSheen}; ${durable ? `${line.name} keeps a class-A scrub rating even at this lower sheen.` : `eggshell balances washability and a soft finish.`}`
    );
  }
  reasoning.push(`Trim steps up to ${trimSheen} (S-01: trim at least one sheen above walls). Ceiling stays flat white.`);
  reasoning.push(
    badge === "minor"
      ? `Runner-up ${alt.name} (LRV ${alt.lrv}) is within a hair; this is a low-stakes fork, pick on preference.`
      : `Runner-up ${alt.name} (LRV ${alt.lrv}); confidence ${confidence}, ${badge} decision.`
  );

  return {
    mood, moodCopy: MOOD_COPY[mood], wall: top, line, runnerUp: alt,
    baseSheen, wallSheen, recommendedSheen, sheenBelowWetFloor, trimSheen, steppedDown, lrvFloor,
    targetLrv: input.targetLrv ?? null,
    harmony, confidence, badge,
    ranked: ranked.slice(0, 5).map((x) => ({ name: x.c.name, code: x.c.code, score: +x.score.toFixed(3), hardFiltered: false })),
    hardFilteredCount, reasoning,
  };
}
